import {
  CaseStatus,
  useGetCasesQuery,
  PageInfo,
  useGetTicketingIntegrationsQuery,
} from "~/operations";
import { Space } from "~/lib/types";
import { ListCaseEdges } from "~/pages/tickets/types";
import { TabOption } from "~/components/ui-library";
import { SyntheticEvent, useCallback, useState } from "react";
import { CASES_TABS } from "~/pages/tickets/constants";
import { getAssetMrn } from "~/lib/urlUtils";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { usePaginate } from "~/components/pagination/usePaginate";
import { PaginateProps } from "~/components/pagination/paginate";

type UseCasesPageProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  assetId: string | null;
};

type UseCasesPageReturn = {
  closedCases: ListCaseEdges;
  openCases: ListCaseEdges;
  hasTicketingIntegration: boolean;
  closedCasesDataLoading: boolean;
  openCasesDataLoading: boolean;
  showCreateFirstCaseScreen: boolean;
  showCreateTicketingIntegrationScreen: boolean;
  hasCases: boolean;
  tabList: TabOption[];
  currentTab: number;
  openCasesTotalCount: number;
  closedCasesTotalCount: number;
  onTabChange: (_event: SyntheticEvent, newValue: number) => void;
  closedCasesPaginateProps: PaginateProps;
  openCasesPaginateProps: PaginateProps;
};

export function useTicketsPage({
  space,
  scope,
  assetId,
}: UseCasesPageProps): UseCasesPageReturn {
  const [currentTab, setCurrentTab] = useState(CASES_TABS.OPEN);
  const openTicketsPaginate = usePaginate();
  const closedTicketsPaginate = usePaginate();
  const handleChangeTab = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
    },
    [],
  );

  const assetMrn = getAssetMrn(space.id, assetId);

  const {
    data: closedCasesData,
    loading: closedCasesDataLoading,
    fetchMore: fetchMoreClosedCases,
  } = useGetCasesQuery({
    variables: {
      ...closedTicketsPaginate.pageVars,
      input: {
        scopeMrn: scope.mrn,
        statuses: [CaseStatus.Closed],
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: openCasesData,
    loading: openCasesDataLoading,
    fetchMore: fetchMoreOpenCases,
  } = useGetCasesQuery({
    variables: {
      ...openTicketsPaginate.pageVars,
      input: {
        contextMrn: assetMrn ? scope.mrn : "",
        scopeMrn: assetMrn || scope.mrn,
        statuses: [CaseStatus.Open],
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const { hasTicketingIntegration } = useTicketingIntegrations();

  const closedCases = closedCasesData?.cases?.edges || [];
  const closedCasesPageInfo = closedCasesData?.cases?.pageInfo;
  const closedCasesPaginateProps =
    closedTicketsPaginate.pageProps(closedCasesPageInfo);
  const closedCasesTotalCount = closedCasesData?.cases?.totalCount || 0;
  const openCases = openCasesData?.cases?.edges || [];
  const openCasesPageInfo = openCasesData?.cases?.pageInfo;
  const openCasesPaginateProps =
    openTicketsPaginate.pageProps(openCasesPageInfo);
  const openCasesTotalCount = openCasesData?.cases?.totalCount || 0;

  const showCreateFirstCaseScreen =
    closedCases.length === 0 &&
    openCases.length === 0 &&
    hasTicketingIntegration &&
    !openCasesDataLoading &&
    !closedCasesDataLoading;

  const showCreateTicketingIntegrationScreen =
    !hasTicketingIntegration &&
    closedCases.length === 0 &&
    openCases.length === 0 &&
    !closedCasesDataLoading &&
    !openCasesDataLoading;

  const hasCases = closedCases.length > 0 || openCases.length > 0;

  const tabList: TabOption[] = [
    { label: `Open Tickets (${openCasesTotalCount})`, value: "open" },
    { label: `Closed Tickets (${closedCasesTotalCount})`, value: "closed" },
  ];

  return {
    closedCases,
    openCases,
    hasTicketingIntegration,
    openCasesDataLoading: openCasesDataLoading && !openCasesData,
    closedCasesDataLoading,
    showCreateFirstCaseScreen,
    showCreateTicketingIntegrationScreen,
    hasCases,
    tabList,
    currentTab,
    onTabChange: handleChangeTab,
    closedCasesTotalCount,
    closedCasesPaginateProps,
    openCasesTotalCount,
    openCasesPaginateProps,
  };
}
